import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { PhSkipForward, PhX } from 'ember-phosphor-icons';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import IconBadge from 'tio-ui/components/icon-badge';
import type { ComponentLike } from '@glint/template';
import type { BetaRecommendation } from 'tio-employee/utils/beta/home-model';

export interface RecommendationItem extends BetaRecommendation {
  /** Fills the card's action area; rendered with no arguments. */
  action: ComponentLike;
  /** The ×. Rejects when the dismissal was not recorded, which keeps the item showing. */
  onDismiss: () => Promise<void>;
  /** Remind me later. Rejects like `onDismiss`. */
  onRemindLater: () => Promise<void>;
}

export interface RecommendationCardSignature {
  Args: {
    /** An exit succeeds by its handler leading the caller to drop that item from the list. */
    items: RecommendationItem[];
  };
  Element: HTMLDivElement;
}

/** Pages through the dashboard's recommendations one at a time. */
export default class RecommendationCard extends Component<RecommendationCardSignature> {
  @tracked private position = 0;
  @tracked private failedId?: string;

  /** Modulo, so an item leaving hands its place to the next and the last wraps to the first. */
  get index() {
    return this.position % this.args.items.length;
  }

  get current() {
    return this.args.items[this.index];
  }

  get hasSeveral() {
    return this.args.items.length > 1;
  }

  get ordinal() {
    return this.index + 1;
  }

  get counter() {
    return `${this.ordinal}/${this.args.items.length}`;
  }

  get hasFailed() {
    return this.failedId !== undefined && this.failedId === this.current?.id;
  }

  skip = () => {
    this.failedId = undefined;
    this.position = this.index + 1;
  };

  leave = dropTask(async (exit: 'onDismiss' | 'onRemindLater') => {
    const item = this.current;
    if (!item) return;

    this.failedId = undefined;
    try {
      await item[exit]();
    } catch {
      this.failedId = item.id;
    }
  });

  dismiss = () => this.leave.perform('onDismiss');

  remindLater = () => this.leave.perform('onRemindLater');

  <template>
    {{#if this.current}}
      {{#let this.current as |item|}}
        <Card class="tio-col-loose" data-test-recommendation-card={{item.id}} ...attributes>
          <div class="tio-row items-center">
            {{#if this.hasSeveral}}
              <span
                class="tio-text-sm-medium tio-fg-muted"
                aria-hidden="true"
                data-test-recommendation-counter
              >
                {{this.counter}}
              </span>
              <span class="sr-only">{{t
                  "recommendation_card.position"
                  position=this.ordinal
                  count=@items.length
                }}</span>
              <Button
                @appearance="minimal"
                @size="sm"
                @icon={{PhSkipForward}}
                @ariaLabel={{t "recommendation_card.next"}}
                @testHook="recommendation-card__next"
                disabled={{this.leave.isRunning}}
                {{on "click" this.skip}}
                data-test-recommendation-next
              />
            {{/if}}
            <Button
              class="ml-auto"
              @appearance="minimal"
              @size="sm"
              @icon={{PhX}}
              @ariaLabel={{t "recommendation_card.dismiss"}}
              @testHook="recommendation-card__dismiss"
              disabled={{this.leave.isRunning}}
              {{on "click" this.dismiss}}
              data-test-recommendation-dismiss
            />
          </div>

          <div class="tio-row items-start">
            <IconBadge @icon={{item.icon}} @color="purple" />
            <div class="tio-col-loose grow min-w-0">
              <div class="tio-col-tight">
                <Heading @level={{3}} @size="base">{{item.title}}</Heading>
                <p class="tio-text-sm-normal">{{item.description}}</p>
              </div>
              {{#let item.action as |Action|}}
                <Action />
              {{/let}}
              <div>
                <Button
                  @appearance="outlined"
                  @size="sm"
                  @isRunning={{this.leave.isRunning}}
                  @testHook="recommendation-card__remind-later"
                  {{on "click" this.remindLater}}
                  data-test-recommendation-remind-later
                >{{t "recommendation_card.remind_later"}}</Button>
              </div>
              {{#if this.hasFailed}}
                <AlertBanner @intent="danger" @size="sm" data-test-recommendation-error>
                  {{t "recommendation_card.exit_failed"}}
                </AlertBanner>
              {{/if}}
            </div>
          </div>
        </Card>
      {{/let}}
    {{/if}}
  </template>
}
